import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { baseAPI } from '../../../config';
import {
  TASK_TYPES,
  ASSIGNMENT_STATUSES,
  ASSIGNMENT_PRIORITIES,
  ASSIGNMENT_STATUS_LABELS,
  ASSIGNMENT_PRIORITY_LABELS,
  TASK_TYPE_LABELS,
} from '@claimup/shared/constants/assignments';

/** Campos editables en masa. `notes` queda fuera a propósito (texto por caso). */
const FIELDS = [
  { value: 'assigned_to', label: 'Asignado a' },
  { value: 'status', label: 'Estado' },
  { value: 'priority', label: 'Prioridad' },
  { value: 'task_type', label: 'Tipo' },
  { value: 'date', label: 'Fecha' },
  { value: 'description', label: 'Descripción' },
];

const selectClass =
  'form-select text-sm border-slate-300 rounded-lg focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50';
const inputClass =
  'form-input text-sm border-slate-300 rounded-lg focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50';

/**
 * Barra de acciones en masa. Se pueden encadenar varios campos en una sola
 * operación: se van añadiendo a una lista de cambios pendientes y se aplican
 * juntos, de modo que los assignments se recorren una vez y la confirmación
 * enseña todo lo que va a pasar de golpe.
 */
const BulkActionsBar = ({ selectedCount, capped, maxBulkSize, onClear, onApply, applying }) => {
  const [field, setField] = useState('');
  const [users, setUsers] = useState([]);
  // Cambios ya añadidos: [{ field, changes, description }]
  const [staged, setStaged] = useState([]);

  // Valores por tipo de campo
  const [scalarValue, setScalarValue] = useState('');
  const [dateMode, setDateMode] = useState('shift');
  const [shiftAmount, setShiftAmount] = useState('7');
  const [shiftUnit, setShiftUnit] = useState('days');
  const [dateValue, setDateValue] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${baseAPI}/users/list?role=admin`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setUsers(response.data || []);
      } catch (error) {
        console.error('Error fetching users for bulk actions:', error);
      }
    };
    fetchUsers();
  }, []);

  // Al cambiar de campo se descarta el valor anterior: evita aplicar sin querer
  // un valor que se eligió para otro campo.
  const handleFieldChange = value => {
    setField(value);
    setScalarValue('');
    setDescription('');
    setDateValue('');
  };

  /** `changes` para el backend, o null si falta el valor. */
  const buildChanges = () => {
    if (field === 'date') {
      if (dateMode === 'shift') {
        const amount = Number(shiftAmount);
        if (!Number.isFinite(amount) || amount === 0) return null;
        return { date: { mode: 'shift', [shiftUnit]: amount } };
      }
      if (!dateValue) return null;
      return { date: { mode: 'set', value: new Date(dateValue).toISOString() } };
    }

    if (field === 'description') {
      return description.trim() ? { description: description.trim() } : null;
    }

    if (field === 'assigned_to') {
      if (scalarValue === '') return null;
      return { assigned_to: scalarValue === 'unassigned' ? null : scalarValue };
    }

    return scalarValue ? { [field]: scalarValue } : null;
  };

  /** Descripción en español del cambio, para el modal de confirmación. */
  const describeChanges = () => {
    if (field === 'date') {
      if (dateMode === 'shift') {
        const amount = Number(shiftAmount);
        const unit = shiftUnit === 'days' ? 'día(s)' : 'hora(s)';
        return amount >= 0
          ? `Posponer la fecha ${amount} ${unit}`
          : `Adelantar la fecha ${Math.abs(amount)} ${unit}`;
      }
      return `Fijar la fecha en ${new Date(dateValue).toLocaleString('es-ES')}`;
    }
    if (field === 'description') return `Cambiar la descripción a "${description.trim()}"`;
    if (field === 'assigned_to') {
      if (scalarValue === 'unassigned') return 'Dejar sin asignar';
      if (scalarValue === 'AIAgent') return 'Asignar a la IA (automática)';
      const user = users.find(u => u._id === scalarValue);
      const name = user ? `${user.username || ''} ${user.userlastname || ''}`.trim() : scalarValue;
      return `Asignar a ${name}`;
    }
    if (field === 'status') return `Cambiar el estado a "${ASSIGNMENT_STATUS_LABELS[scalarValue]}"`;
    if (field === 'priority') return `Cambiar la prioridad a "${ASSIGNMENT_PRIORITY_LABELS[scalarValue]}"`;
    if (field === 'task_type') return `Cambiar el tipo a "${TASK_TYPE_LABELS[scalarValue]}"`;
    return '';
  };

  const currentChanges = field ? buildChanges() : null;

  /** Añade el campo en edición a la lista; un mismo campo solo puede ir una vez. */
  const handleAddField = () => {
    if (!currentChanges) return;
    const entry = { field, changes: currentChanges, description: describeChanges() };
    setStaged(prev => [...prev.filter(s => s.field !== field), entry]);
    handleFieldChange('');
  };

  const handleRemoveStaged = removedField => {
    setStaged(prev => prev.filter(s => s.field !== removedField));
  };

  // Lo que se envía: los cambios añadidos + el que esté en edición (para no
  // obligar a pulsar "Añadir" cuando solo se modifica un campo).
  const allEntries = [...staged.filter(s => s.field !== field), ...(currentChanges ? [{ field, changes: currentChanges, description: describeChanges() }] : [])];
  const mergedChanges = allEntries.length
    ? allEntries.reduce((acc, entry) => ({ ...acc, ...entry.changes }), {})
    : null;
  const descriptions = allEntries.map(entry => entry.description);

  const renderValueControl = () => {
    if (!field) return null;

    if (field === 'assigned_to') {
      return (
        <select value={scalarValue} onChange={e => setScalarValue(e.target.value)} className={selectClass}>
          <option value=''>Selecciona…</option>
          <option value='unassigned'>Sin asignar</option>
          <option value='AIAgent'>IA (automática)</option>
          {users.map(user => (
            <option key={user._id} value={user._id}>
              {`${user.username || ''} ${user.userlastname || ''}`.trim() || user.usermail}
            </option>
          ))}
        </select>
      );
    }

    if (field === 'status' || field === 'priority' || field === 'task_type') {
      const options = {
        status: ASSIGNMENT_STATUSES.map(v => ({ value: v, label: ASSIGNMENT_STATUS_LABELS[v] })),
        priority: ASSIGNMENT_PRIORITIES.map(v => ({ value: v, label: ASSIGNMENT_PRIORITY_LABELS[v] })),
        task_type: TASK_TYPES.map(v => ({ value: v, label: TASK_TYPE_LABELS[v] })),
      }[field];

      return (
        <select value={scalarValue} onChange={e => setScalarValue(e.target.value)} className={selectClass}>
          <option value=''>Selecciona…</option>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    if (field === 'date') {
      return (
        <div className='flex items-center gap-2'>
          <select value={dateMode} onChange={e => setDateMode(e.target.value)} className={selectClass}>
            <option value='shift'>Posponer / adelantar</option>
            <option value='set'>Fijar fecha</option>
          </select>
          {dateMode === 'shift' ? (
            <>
              <input
                type='number'
                value={shiftAmount}
                onChange={e => setShiftAmount(e.target.value)}
                className={`${inputClass} w-20`}
              />
              <select value={shiftUnit} onChange={e => setShiftUnit(e.target.value)} className={selectClass}>
                <option value='days'>días</option>
                <option value='hours'>horas</option>
              </select>
            </>
          ) : (
            <input
              type='datetime-local'
              value={dateValue}
              onChange={e => setDateValue(e.target.value)}
              className={inputClass}
            />
          )}
        </div>
      );
    }

    return (
      <input
        type='text'
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder='Nueva descripción'
        maxLength={500}
        className={`${inputClass} w-64`}
      />
    );
  };

  return (
    <div className='mt-4 p-4 bg-primary-50 border border-primary-200 rounded-lg'>
      <div className='flex flex-wrap items-center gap-3'>
        <div className='flex items-center gap-2'>
          <span className='text-sm font-semibold text-slate-800'>
            {selectedCount} seleccionado{selectedCount === 1 ? '' : 's'}
          </span>
          <button
            onClick={onClear}
            className='text-slate-500 hover:text-slate-700 inline-flex items-center text-sm'
            title='Limpiar selección'
          >
            <XMarkIcon className='w-4 h-4' />
          </button>
        </div>

        <div className='h-6 w-px bg-primary-200' />

        <select value={field} onChange={e => handleFieldChange(e.target.value)} className={selectClass}>
          <option value=''>Campo a modificar…</option>
          {FIELDS.map(f => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>

        {renderValueControl()}

        {field && (
          <button
            onClick={handleAddField}
            disabled={!currentChanges}
            className='btn border-primary-300 text-primary-600 hover:border-primary-400 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1'
            title='Añadir este campo y seguir con otro'
          >
            <PlusIcon className='w-4 h-4' />
            Añadir otro campo
          </button>
        )}

        <button
          onClick={() => onApply(mergedChanges, descriptions)}
          disabled={!mergedChanges || applying}
          className='btn bg-primary-500 hover:bg-primary-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed'
        >
          {applying ? 'Aplicando…' : 'Aplicar'}
        </button>
      </div>

      {staged.length > 0 && (
        <div className='mt-3 flex flex-wrap items-center gap-2'>
          <span className='text-xs uppercase font-semibold text-slate-500'>Cambios a aplicar:</span>
          {staged.map(entry => (
            <span
              key={entry.field}
              className='inline-flex items-center gap-1 px-2 py-1 bg-white border border-primary-200 rounded-lg text-xs text-slate-700'
            >
              {entry.description}
              <button
                onClick={() => handleRemoveStaged(entry.field)}
                className='text-slate-400 hover:text-slate-600'
                title='Quitar este cambio'
                aria-label={`Quitar ${entry.description}`}
              >
                <XMarkIcon className='w-3 h-3' />
              </button>
            </span>
          ))}
        </div>
      )}

      {capped && (
        <p className='mt-2 text-xs text-amber-700'>
          La selección supera el máximo: solo se modificarán los primeros {maxBulkSize} assignments. Afina el filtro
          para trabajar por lotes.
        </p>
      )}
    </div>
  );
};

export default BulkActionsBar;
